// Results of a "one ad with all the videos" test: per flow, how Meta split the budget between
// the videos and what each brought (from the ad's video_asset breakdown), with the winner
// marked once there's enough data. Signups/paying are per ad (they can't be split by video).
import {FC, useEffect, useState} from 'react'
import {AdTest, AssetRow, Creative, getTestAssets, Launch} from './_requests'

const ils = (n: number | null | undefined) =>
  n == null || !isFinite(n) ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`
const FLOW: Record<string, string> = {website: '🌐 דף הרשמה', lead_form: '📝 טופס פייסבוק'}

const MultiResult: FC<{test: AdTest; byKey: Record<string, Creative>; launches: Launch[]}> = ({
  test,
  byKey,
  launches,
}) => {
  const [rows, setRows] = useState<Record<string, AssetRow[]> | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    getTestAssets(test._id)
      .then(({data}) => setRows(data.by_destination))
      .catch((e) => setError(e?.response?.data?.error || e.message))
  }, [test._id])

  if (error) return <div className='text-danger fs-8'>{error}</div>
  if (!rows) return <div className='text-muted fs-8'>טוען תוצאות לפי סרטון…</div>

  return (
    <div className='d-grid gap-4'>
      {test.destinations.map((d) => {
        const ad = launches.find((l) => l.destination === d)
        const list = (rows[d] || []).slice().sort((a, b) => b.spend - a.spend)
        const result = (r: AssetRow) => (d === 'lead_form' ? r.leads : r.registrations)
        const cost = (r: AssetRow) => (result(r) ? r.spend / result(r) : null)
        const ranked = list
          .filter((r) => result(r) >= 3 && cost(r) != null)
          .sort((a, b) => cost(a)! - cost(b)!)
        const best = ranked[0]
        const totalSpend = list.reduce((n, r) => n + r.spend, 0)
        return (
          <div key={d} className='border rounded p-4'>
            <div className='d-flex justify-content-between flex-wrap gap-2 mb-2'>
              <b>{FLOW[d] || d} — מודעה אחת</b>
              <span className='fs-8 text-muted'>
                {ad?.status === 'creating'
                  ? 'נבנית…'
                  : ad?.status === 'failed'
                  ? `נכשלה: ${ad.error}`
                  : ad?.meta?.effective_status || ad?.status}
                {ad
                  ? ` · ${ad.funnel.signups} הרשמות · ${ad.funnel.paid} שילמו · ${ils(
                      ad.funnel.revenue
                    )} הכנסה`
                  : ''}
              </span>
            </div>
            {list.length === 0 ? (
              <div className='text-muted fs-8'>
                עוד אין נתונים — התוצאות לפי סרטון מופיעות אחרי שהמודעה רצה.
              </div>
            ) : (
              <table className='table table-sm table-row-bordered align-middle fs-8 mb-0'>
                <thead>
                  <tr className='text-muted fw-bold'>
                    <th>סרטון</th>
                    <th>חלק מהתקציב</th>
                    <th>הוצאה</th>
                    <th>קליקים</th>
                    <th>{d === 'lead_form' ? 'לידים' : 'הרשמות (פייסבוק)'}</th>
                    <th>עלות ל{d === 'lead_form' ? 'ליד' : 'הרשמה'}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => {
                    const c = r.creative_key ? byKey[r.creative_key] : undefined
                    return (
                      <tr key={r.video_id} style={best === r ? {background: '#ecfdf3'} : undefined}>
                        <td>
                          <div className='d-flex align-items-center gap-2'>
                            <div
                              style={{
                                width: 27,
                                height: 48,
                                borderRadius: 4,
                                flex: '0 0 auto',
                                background: `#000 url("${c?.thumb || ''}") center/cover`,
                              }}
                            />
                            <span className='fw-bold'>
                              {c?.title || `סרטון ${r.video_id}`}{' '}
                              {best === r && <span className='badge badge-success'>🏆 מנצח</span>}
                            </span>
                          </div>
                        </td>
                        <td>{totalSpend ? Math.round((r.spend / totalSpend) * 100) + '%' : '—'}</td>
                        <td>{ils(r.spend)}</td>
                        <td>{r.clicks}</td>
                        <td>{result(r)}</td>
                        <td className='fw-bold'>{ils(cost(r))}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
      <div className='text-muted fs-8'>
        במודעה אחת פייסבוק מחליט כמה כל סרטון מקבל — סרטון שקיבל מעט תקציב עוד לא "הפסיד". מנצח
        מסומן רק אחרי 3+ תוצאות.
      </div>
    </div>
  )
}

export default MultiResult
